import { Button, Stack, useTheme } from "@mui/material";
import CreateOutlined from "@mui/icons-material/CreateOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";

import BigPot from "src/assets/icons/SsqTitleIcon/big-pot.svg?react";
import BigBox from "src/assets/icons/SsqTitleIcon/big-box.svg?react";
import BigCutlery from "src/assets/icons/SsqTitleIcon/big-cutlery.svg?react";
import BigMarket from "src/assets/icons/SsqTitleIcon/big-market.svg?react";
// import BigProduction from "src/assets/icons/SsqTitleIcon/big-production.svg?react";

import { GenericAccordion, useFormBaseContext } from "@foodpilot/foods";

import { useTranslation } from "react-i18next";
import { ProductFormType } from "../ProductForm.types";
import { ProductInformationSummary } from "src/components/Summary/ProductSummary/Information";
import { ProductRecipeSummary } from "src/components/Summary/ProductSummary/Recipe";
import { ProductPackagingsSummary } from "src/components/Summary/ProductSummary/Packaging";
import { ProductConsumptionSummary } from "src/components/Summary/ProductSummary/Consumption";
import { ProductDistributionSummary } from "src/components/Summary/ProductSummary/Distribution";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
// import { ProductTransformationSummary } from "src/components/ProductSummary/Production";

type ModifyButtonProps = {
    pageNumber: number;
};
const ModifyButton = (props: ModifyButtonProps) => {
    const { setPage } = useFormBaseContext();
    const { t } = useTranslation();

    const modifyText = t("common.actions.edit", "Modify");
    return (
        <Button
            variant="white-filled"
            startIcon={<CreateOutlined />}
            sx={{ marginRight: "24px", borderRadius: "44px" }}
            onClick={() => setPage(props.pageNumber)}
        >
            {modifyText}
        </Button>
    );
};

type Page7Props = {
    product: ProductFormType;
};

export const Page7 = (props: Page7Props) => {
    const { t } = useTranslation();
    const { product } = props;
    const theme = useTheme();
    const { isFeatureActivated } = useApplicationParameters();
    const isProductConsumptionEnabled = isFeatureActivated("FS-product-consumption");
    const isProductDistributionEnabled = isFeatureActivated("FS-product-distribution");
    const isPackagingEnabled = isFeatureActivated("FS-entity-packagings");

    // Page indices in ProductForm follow the flag-gated order:
    //   Page1=0, Page2=1, Page3=2 when packaging is on,
    //   Page5 (distribution) follows the last mounted product-data page,
    //   Page6 (consumption)  at the next index when its flag is on.
    const distributionPageIndex = isPackagingEnabled ? 3 : 2;
    const consumptionPageIndex = distributionPageIndex + (isProductDistributionEnabled ? 1 : 0);

    const acc: Set<string> = new Set();
    const productSuppliersName = product.productIngredients.reduce((previousValue, currentValue) => {
        // Les produits intermédiaires n'ont pas de fournisseurs, on ne traite que les vrais ingrédients
        if (currentValue.ingredient && currentValue.ingredient.suppliersName) {
            const names = currentValue.ingredient.suppliersName;
            names.forEach((name) => previousValue.add(name.name));
        }

        return previousValue;
    }, acc);
    const sortedSuppliersName = Array.from(productSuppliersName).toSorted();
    return (
        <div>
            <Stack direction="column" useFlexGap gap="8px" paddingTop={theme.spacing(4)}>
                <GenericAccordion
                    headerIcon={<InfoOutlined />}
                    headerText={t("common.general_information", "General information")}
                    controls={<ModifyButton pageNumber={0} />}
                >
                    <ProductInformationSummary
                        title={product.title}
                        clientId={product.clientId}
                        productLine={product.productLine?.name}
                        productReference={product.productReference}
                        brandName={product.brand?.name}
                        netWeight={product.netWeight}
                        organic={product.organic}
                        tags={product.tags ? Object.values(product.tags).flat() : []}
                    />
                </GenericAccordion>

                <GenericAccordion
                    headerIcon={<BigPot />}
                    headerText={t("product.recipe.title", "Recipe")}
                    controls={<ModifyButton pageNumber={1} />}
                >
                    <ProductRecipeSummary
                        productIngredients={product.productIngredients}
                        suppliersName={sortedSuppliersName}
                    />
                </GenericAccordion>

                {isPackagingEnabled && (
                    <GenericAccordion
                        headerIcon={<BigBox />}
                        headerText={t("sidebar.packagings.title", "Packaging")}
                        controls={<ModifyButton pageNumber={2} />}
                    >
                        <ProductPackagingsSummary productPackagings={product.productPackagings} />
                    </GenericAccordion>
                )}

                {/* <GenericAccordion
                    headerIcon={<BigProduction />}
                    headerText={t("product.production.title", "Production")}
                    controls={<ModifyButton pageNumber={3} />}
                >
                    <ProductTransformationSummary productTransformations={product.productTransformations} />
                </GenericAccordion> */}

                {isProductDistributionEnabled && (
                    <GenericAccordion
                        headerIcon={<BigMarket />}
                        headerText={t("product.distribution.title", "Distribution")}
                        controls={<ModifyButton pageNumber={distributionPageIndex} />}
                    >
                        <ProductDistributionSummary
                            distribution={product.distribution}
                            distributionReferenceId={product.distributionReferenceId}
                        />
                    </GenericAccordion>
                )}

                {isProductConsumptionEnabled && (
                    <GenericAccordion
                        headerIcon={<BigCutlery />}
                        headerText={t("product.consumption.title", "Consumption")}
                        controls={<ModifyButton pageNumber={consumptionPageIndex} />}
                    >
                        <ProductConsumptionSummary productConsumptions={product.productConsumptions} />
                    </GenericAccordion>
                )}
            </Stack>
        </div>
    );
};
